import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, LogOut, Loader2, Search } from "lucide-react";
import { SubscriberTable } from "./SubscriberTable";
import { OrdersTable } from "./OrdersTable";
import { ProductsTab } from "./ProductsTab";
import { CouponsTab } from "./CouponsTab";
import { InboxTab } from "./InboxTab";
import { ReviewsTab } from "./ReviewsTab";
import { ShippingTab } from "./ShippingTab";
import { AmbassadorsTab } from "./AmbassadorsTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SubscriberDashboard = ({ adminKey, onLogout }) => {
  const [tab, setTab] = useState("subscribers");
  const [subs, setSubs] = useState(null);
  const [query, setQuery] = useState("");
  const [exporting, setExporting] = useState(null); // "subscribers" | "buyers" | null
  const headers = useMemo(() => ({ Authorization: `Bearer ${adminKey}` }), [adminKey]);

  useEffect(() => {
    axios
      .get(`${API}/admin/subscribers`, { headers })
      .then((r) => setSubs(r.data))
      .catch((err) => {
        if (err?.response?.status === 401) onLogout();
        else toast.error("Could not load subscribers.");
      });
  }, [headers, onLogout]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!subs) return [];
    return q ? subs.filter((s) => s.email.includes(q) || (s.name || "").toLowerCase().includes(q)) : subs;
  }, [subs, query]);

  const remove = async (id) => {
    await axios.delete(`${API}/admin/subscribers/${id}`, { headers });
    setSubs((list) => list.filter((s) => s.id !== id));
    toast.success("Subscriber removed.");
  };

  const exportCsv = async (kind = "subscribers") => {
    setExporting(kind);
    try {
      const path = kind === "buyers" ? "orders/export" : "subscribers/export";
      const r = await axios.get(`${API}/admin/${path}`, { headers, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = kind === "buyers" ? "deen-district-buyers.csv" : "deen-district-drop001-subscribers.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success(kind === "buyers" ? "Buyers CSV downloaded." : "Subscribers CSV downloaded.");
    } catch {
      toast.error("Export failed.");
    } finally {
      setExporting(null);
    }
  };

  return (
    <div data-testid="admin-dashboard" className="max-w-5xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
        <div>
          <span className="font-mono-d text-[10px] uppercase tracking-[0.25em] text-[#ff2a85]">Drop 001 · Admin</span>
          <h1 className="font-display font-extrabold uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95] mt-3 tracking-tight">
            The <span className="text-pink">List</span>
          </h1>
          <p data-testid="admin-total-count" className="mt-4 text-[#5f5e5a] text-sm font-mono-d uppercase tracking-[0.2em]">
            {subs === null ? "Loading…" : `${subs.length.toLocaleString()} waiting on the drop`}
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            data-testid="admin-export-button"
            onClick={() => exportCsv("subscribers")}
            disabled={!!exporting || !subs?.length}
            className="h-12 px-6 rounded-full bg-[#ff2a85] hover:bg-[#e60067] disabled:opacity-50 text-white font-mono-d text-[11px] uppercase tracking-[0.2em] transition-colors flex items-center gap-2"
          >
            {exporting === "subscribers" ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Subscribers CSV
          </button>
          <button
            data-testid="admin-export-buyers-button"
            onClick={() => exportCsv("buyers")}
            disabled={!!exporting}
            title="Everyone who has paid for an order — name, email, phone, location, spend. For email marketing."
            className="h-12 px-6 rounded-full bg-[#0a0a0b] hover:bg-[#ff2a85] disabled:opacity-50 text-white font-mono-d text-[11px] uppercase tracking-[0.2em] transition-colors flex items-center gap-2"
          >
            {exporting === "buyers" ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Buyers CSV
          </button>
          <button
            data-testid="admin-logout-button"
            onClick={onLogout}
            className="h-12 px-5 rounded-full border border-[#e3e2dc] hover:border-[#0a0a0b]/40 text-[#5f5e5a] hover:text-[#0a0a0b] font-mono-d text-[11px] uppercase tracking-[0.2em] transition-colors flex items-center gap-2"
          >
            <LogOut size={14} /> Exit
          </button>
        </div>
      </div>

      <div className="mt-12 flex gap-2 border-b border-[#e3e2dc] overflow-x-auto">
        {[["subscribers", "Subscribers"], ["orders", "Pre-Orders"], ["products", "Products"], ["coupons", "Coupons"], ["ambassadors", "Ambassadors"], ["inbox", "Inbox"], ["reviews", "Reviews"], ["shipping", "Delivery"]].map(([id, label]) => (
          <button
            key={id}
            data-testid={`admin-tab-${id}`}
            onClick={() => setTab(id)}
            className={`px-5 py-3 font-mono-d text-[11px] uppercase tracking-[0.2em] border-b-2 -mb-px whitespace-nowrap transition-colors ${
              tab === id ? "border-[#ff2a85] text-[#0a0a0b]" : "border-transparent text-[#5f5e5a] hover:text-[#0a0a0b]"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "orders" ? (
        <OrdersTable headers={headers} />
      ) : tab === "products" ? (
        <ProductsTab headers={headers} />
      ) : tab === "coupons" ? (
        <CouponsTab headers={headers} />
      ) : tab === "ambassadors" ? (
        <AmbassadorsTab headers={headers} />
      ) : tab === "inbox" ? (
        <InboxTab headers={headers} />
      ) : tab === "reviews" ? (
        <ReviewsTab headers={headers} />
      ) : tab === "shipping" ? (
        <ShippingTab headers={headers} />
      ) : (
        <>
      <div className="relative mt-8">
        <Search size={16} className="absolute left-5 top-1/2 -translate-y-1/2 text-[#5f5e5a]" />
        <input
          data-testid="admin-search-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by email or name"
          className="w-full h-14 pl-12 pr-5 rounded-full bg-[#faf9f6] border border-[#e3e2dc] text-sm outline-none focus:border-[#ff2a85]/60 transition-colors"
        />
      </div>

      {subs === null ? (
        <div className="py-24 flex justify-center"><Loader2 className="animate-spin text-[#5f5e5a]" /></div>
      ) : (
        <SubscriberTable rows={filtered} onDelete={remove} />
      )}
        </>
      )}
    </div>
  );
};
